import { FeatureIdEnum } from '@anecdotes/api-contracts';
import { FeatureModel, FeaturesService } from '@anecdotes/feature-management';
import { toDictionary } from '@anecdotes/utils';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, Provider } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, firstValueFrom, map, of } from 'rxjs';

const providersList: Provider[] = [];

@Injectable()
class FeaturesServiceOverride {
  private httpClient: HttpClient;
  
  private featuresDictionary$ = new BehaviorSubject<{ [featureId: string]: FeatureModel }>(null);

  constructor(httpBackend: HttpBackend) {
    this.httpClient = new HttpClient(httpBackend);
  }

  async initAsync(): Promise<void> {
    if (!this.featuresDictionary$.value) {
      // This workaround is for Cypress that will intercept this request and return test related features
      const features = await firstValueFrom(
        this.httpClient.get<FeatureModel[]>('featureManagement/features.json').pipe(catchError(() => of([]))),
      ); 
      
      this.featuresDictionary$.next(toDictionary(features, (feature) => feature.featureId));
    }
  }

  getEntitlement(featureId: FeatureIdEnum): Observable<FeatureModel> {
    return this.featuresDictionary$.pipe(filter(Boolean), map(() => this.getEntitlementSync(featureId)));
  }

  getEntitlementSync(featureId: FeatureIdEnum): FeatureModel {
    if (this.featuresDictionary$.value && this.featuresDictionary$.value[featureId]) {
      return this.featuresDictionary$.value[featureId];
    }
    
    return { featureId: featureId, hasAccess: false } as FeatureModel;
  }

  getEntitlements(): Observable<FeatureModel[]> {
    return this.featuresDictionary$.pipe(
      filter((features) => !!features),
      map((featuresDictionary) => Object.values(featuresDictionary) as FeatureModel[]),
    );
  }
}

// This is replacement of FeaturesService so that Cypress tests could setup customer features
// without Stigg SDK participation.
// This will work ONLY in development build configuration.
if (!window['__AnPlatformConfiguration__'].stigg) {
  providersList.push({ provide: FeaturesService, useClass: FeaturesServiceOverride });
}

export const featureManagementProviders: Provider[] = providersList;
